import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  GitMerge,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Train,
  Bus,
  Compass,
  AlertTriangle,
  ChevronDown
} from 'lucide-react';
import baseapi, { useApiOutage } from '../baseapi';
import { EMPTY_SUMMARY } from '../config/emptySummary';
import CameraStreamModal from '../components/junctions/CameraStreamModal';
import NetworkFlowBoard, { type LinkTime } from '../components/junctions/NetworkFlowBoard';

interface CameraInfo {
  id: string;
  state: string;
  detail: string;
  source: string;
  frame_age_s: number;
  proc_fps: number;
  decode_fps: number;
  decoder: string;
  frames_received: number;
  frames_processed: number;
  frame_size: [number, number];
  tracks: number;
}

interface DirectionMetric {
  m1: number;
  m5: number;
  m15: number;
  total: number;
}

interface CheckpointData {
  id: string;
  name: string;
  kind: string;
  hub?: string;
  coverage: string;
  cameras: CameraInfo[];
  people?: {
    toward?: DirectionMetric;
    away?: DirectionMetric;
  };
  vehicles?: {
    toward?: Record<string, DirectionMetric>;
    away?: Record<string, DirectionMetric>;
  };
  lines?: Record<string, any>;
}

interface SequenceGroup {
  id: string;
  name: string;
  sequence: string[];
}

interface SummaryResponse {
  server_time: number;
  reporting_start: number;
  reporting?: {
    timezone: string;
    daily_reset_hour: number;
  };
  headline: {
    rail_out?: DirectionMetric;
    rail_in?: DirectionMetric;
    bus_out?: DirectionMetric;
    bus_in?: DirectionMetric;
    kummari_toward?: DirectionMetric;
    final_in?: DirectionMetric;
    final_out?: DirectionMetric;
  };
  checkpoints: Record<string, CheckpointData>;
  groups?: SequenceGroup[];
  engine?: {
    device: string;
    loop_fps: number;
    batch_ms: number;
  };
}

const fmt = (n?: number) => (n !== undefined ? Math.round(n).toLocaleString('en-IN') : '—');

/** Minutes from one stage of the board to the next (16 Oct 2026, operators: "how much time to the next flow").
 *  Queue links, measured now: each queue zone on the way holds `count` people and lets `outRate` out per minute, so
 *  a devotee entering waits count / outRate there (the stretch to the Tollgate: its people / what leaves it per
 *  minute); the zones of a route add up. Walking links: the walking route along the streets between the two points
 *  (OpenStreetMap foot router, 16 Oct 2026, WALK_M) at 50 m a minute (3 km/h in a festival crowd) - an estimate, no
 *  camera times it. */
/** Walking distance in metres between two junctions of the board. Measured on 16 Oct 2026 with the OpenStreetMap foot
 *  router (routing.openstreetmap.de) between the board's node positions. Bus station -> Rajiv Gandhi Park and Rajiv
 *  Gandhi Park -> holding area came out 5-6x their straight-line distance (no footpath across on the map, or an
 *  approximate position): for those the straight line x 1.3, the detour typical on this route (1.1-1.4). */
const WALK_M: Record<string, [number, string]> = {
  'STN-MAIN>GADDE': [1306, 'street route'], 'STN-THARA>GADDE': [1041, 'street route'],
  'STN-ET2>GADDE': [1708, 'street route'], 'STN-PARCEL>GADDE': [1144, 'street route'],
  'GADDE>VINAYAKA': [123, 'street route'], 'VINAYAKA>HOLDING': [482, 'street route'],
  'PNBS>RGPARK': [527, 'straight line x 1.3 - no street route on the map'],
  'RGPARK>HOLDING': [204, 'straight line x 1.3 - no street route on the map'],
  'HOLDING>QLINE': [490, 'street route'],
};

function travelTimes(ov: any): Record<string, LinkTime> {
  const out: Record<string, LinkTime> = {};
  if (!ov) return out;
  const z: Record<string, any> = Object.fromEntries((ov.zones || []).map((x: any) => [x.id, x]));
  const st: Record<string, any> = Object.fromEntries((ov.zone_transfers || []).map((x: any) => [x.id, x]));
  const zt = (id: string) => { const q = z[id]; return q && q.count != null && q.outRate >= 1 ? q.count / q.outRate : null; };
  const s = st['Q2-Q3'];
  const stretch = s && s.count != null && s.out_15m >= 15 ? s.count / (s.out_15m / 15) : null;
  const sum = (parts: (number | null)[], names: string): LinkTime | null => parts.every(p => p != null)
    ? { min: (parts as number[]).reduce((a, b) => a + b, 0), kind: 'queue', title: `${names}: people in each zone / the rate it empties, now` } : null;
  const v = sum([zt('Q2'), stretch, zt('Q3'), zt('Q4')], 'Q2 + stretch to the Tollgate + Q3 + Q4');
  const kq = sum([zt('Q1'), stretch, zt('Q3'), zt('Q4')], 'Q1 + stretch to the Tollgate + Q3 + Q4');
  const ex = sum([zt('Q5')], 'Q5 (Antharalayam doors to the exits)');
  if (v) out['QLINE>TEMPLE'] = v;
  if (kq) out['QLINE-K>TEMPLE'] = kq;
  if (ex) out['TEMPLE>EXIT-KDN'] = ex;
  Object.entries(WALK_M).forEach(([k, [m, how]]) => {
    out[k] = { min: m / 50, kind: 'walk', title: `${m.toLocaleString('en-IN')} m on foot (${how}); at 3 km/h in a festival crowd` };
  });
  return out;
}

export const JunctionMonitoringPage: React.FC = () => {
  const [data, setData] = useState<SummaryResponse>(EMPTY_SUMMARY as unknown as SummaryResponse);
  const [loading, setLoading] = useState<boolean>(true);     // true until the first answer: no banner, no figures
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [showSummary, setShowSummary] = useState<boolean>(false);   // the count cards, opened with the Summary button
  const [activeModalCheckpoint, setActiveModalCheckpoint] = useState<CheckpointData | null>(null);
  const [activeModalCameraId, setActiveModalCameraId] = useState<string | undefined>(undefined);
  // all checkpoints of the clicked flow node (camera tabs in the popup)
  const [activeModalGroup, setActiveModalGroup] = useState<CheckpointData[] | undefined>(undefined);
  // false while the page shows the bundled sample file because the live API did not answer
  const [isLive, setIsLive] = useState<boolean>(false);
  const [apiStale, setApiStale] = useState<boolean>(false);
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message

  const openCameraModal = (cp: CheckpointData, camId?: string) => {
    setActiveModalCheckpoint(cp);
    setActiveModalCameraId(camId);
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await baseapi.get('/summary');
      const res = response.data;
      if (res && res.checkpoints && res.headline) {
        setData(res);
        setIsLive(true);
        setApiStale(false);
      } else if (!silent) {
        setData(EMPTY_SUMMARY as unknown as SummaryResponse);
        setIsLive(false);
      }
    } catch (err) {
      console.warn('Live API request failed:', err);
      if (silent) {
        setApiStale(true);                        // keep the last live data on screen, flag it as stale
      } else {
        setData(EMPTY_SUMMARY as unknown as SummaryResponse);
        setIsLive(false);
      }
    } finally {
      setLoading(false);

    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Temple In / Temple Out cards: the same figures as the Overview (GET /api/overview) - devotees past the two
  // queue-line cameras, and people out through the temple exits - so the two pages cannot disagree.
  const [temple, setTemple] = useState<{ inToday: number | null; in15: number | null; outToday: number | null; out15: number | null } | null>(null);
  const [linkTimes, setLinkTimes] = useState<Record<string, LinkTime>>({});
  useEffect(() => {
    let alive = true;
    const load = () => baseapi.get('overview').then(r => {
      const k = r.data?.kpis, t = r.data?.temple;
      if (alive) setLinkTimes(travelTimes(r.data));
      if (alive && k) setTemple({
        inToday: k.total ?? null, in15: k.total_15m ?? null, outToday: k.darshan ?? null,
        out15: t?.outRate != null ? Math.round(t.outRate * 15) : null
      });
    }).catch(() => undefined);
    load();
    const t = window.setInterval(load, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  // Railway station / bus stand points whose cameras have no live video show yesterday's counts at this time of
  // day, marked "Replay · <date>" (GET /api/summary/replay, 14 Oct 2026). Never mixed into a live point.
  const [replay, setReplay] = useState<any>(null);
  useEffect(() => {
    let alive = true;
    const load = () => baseapi.get('/summary/replay').then(r => { if (alive) setReplay(r.data); }).catch(() => undefined);
    load();
    const t = window.setInterval(load, 60000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);
  const view: any = React.useMemo(() => {
    if (!replay?.checkpoints || !data?.checkpoints) return data;
    const isLive = (cp: any) => (cp?.cameras || []).some((c: any) => c.state === 'online');
    const cps: Record<string, any> = { ...(data.checkpoints as any) };
    Object.entries(replay.checkpoints as Record<string, any>).forEach(([id, r]) => {
      const cp = cps[id];
      if (cp && !isLive(cp)) cps[id] = { ...cp, people: r.people, replay: replay.label };
    });
    const hubDown = (hub: string) => {
      const hubs = Object.values(data.checkpoints as any).filter((c: any) => c.kind === 'hub_exit' && c.hub === hub);
      return hubs.length > 0 && hubs.every((c: any) => !isLive(c));
    };
    const h: any = { ...(data.headline as any) };
    const cards: Record<string, string> = {};
    if (hubDown('RAIL')) { h.rail_in = replay.headline.rail_in; h.rail_out = replay.headline.rail_out; cards.rail = replay.label; }
    if (hubDown('BUS')) { h.bus_in = replay.headline.bus_in; h.bus_out = replay.headline.bus_out; cards.bus = replay.label; }
    return { ...data, checkpoints: cps, headline: h, replay_cards: cards };
  }, [data, replay]);
  const replayBadge = (kind: 'rail' | 'bus') => view?.replay_cards?.[kind]
    ? <span className="ml-1 text-[9.5px] font-black px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 uppercase">{view.replay_cards[kind]}</span>
    : null;

  // Live refresh: counts re-fetched every 5 s in the background (no loading flicker). 14 Oct 2026: was 2 s, and a
  // new request went out even while the last one was still waiting - a slow backend got more requests, not fewer
  // (/api/summary recounts the whole day). Now one request at a time per screen.
  const inFlight = useRef(false);
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      if (inFlight.current || document.hidden) return;
      inFlight.current = true;
      fetchData(true).finally(() => { inFlight.current = false; });
    }, 5000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchData]);


  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col gap-2 text-neutral-900 dark:text-neutral-100 pb-1">
      {apiStale && isLive && outage && (
        <div className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-600 dark:text-amber-300 text-xs sm:text-[13px] font-semibold">
          Analytics server not answering (restarting?) — showing the last received counts. Retrying every 5 s.
        </div>
      )}
      {!isLive && !loading && outage && (
        <div className="shrink-0 px-3 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400 text-xs sm:text-[13px] font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Live camera API not reachable — no counts to show. Retrying every 5 s.
        </div>
      )}
      {/* ================= 1. TOP COMMAND & REAL-TIME API STATUS BAR ================= */}
      <div className="shrink-0 p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500 text-white flex items-center justify-center shadow-md shadow-blue-500/20 shrink-0">
            <GitMerge className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-black text-neutral-900 dark:text-neutral-100 tracking-tight">
                Junction Sequential Flow &amp; Transit Monitoring
              </h1>
            </div>
            <p className="text-xs sm:text-[13px] text-neutral-500 dark:text-neutral-400 mt-0.5">
              Corridor sequences from railway station &amp; bus stand leading to temple ghat road &amp; tollgate
            </p>
          </div>
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* (engine badge "ANALYTICS:? · 0 FPS" removed 16 Oct 2026: this backend is API-only, it has no engine figures) */}

          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-2.5 py-1 rounded-md text-xs sm:text-[13px] font-bold border transition-colors cursor-pointer ${autoRefresh
                ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30'
                : 'bg-neutral-100 dark:bg-[#1f2430] text-neutral-500 border-neutral-200 dark:border-[#282e3c]'
              }`}
          >
            Auto: {autoRefresh ? 'ON' : 'OFF'}
          </button>

          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="px-3 py-1 rounded-md bg-blue-500 hover:bg-blue-600 text-white font-bold text-xs sm:text-[13px] shadow-xs flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>{loading ? 'Fetching' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* ================= 2. HEADLINE INFLUX & EXFLUX VELOCITY ROW: hidden by default, opened with this button ================= */}
      <div className="shrink-0 flex items-center">
        <button
          type="button"
          onClick={() => setShowSummary(v => !v)}
          aria-expanded={showSummary}
          title={showSummary ? 'Hide the summary cards' : 'Show the summary cards: railway station, bus station, Kummaripalem, devotees for darshan, darshan completed'}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#181b23] text-xs sm:text-sm font-black text-neutral-700 dark:text-neutral-200 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 shadow-xs transition-colors cursor-pointer"
        >
          <span>Summary</span>
          <ChevronDown className={`w-3.5 h-3.5 stroke-[2.5] transition-transform ${showSummary ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {showSummary && (
      <div className="shrink-0 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {/* Returning to the railway station (people walking into it) */}
        <div title="People walking into the railway station today (returning from the temple)" className="px-2.5 py-2 rounded-lg bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs hover:border-sky-500/40 transition-colors">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-sky-600 dark:text-sky-400 truncate">
              <Train className="w-3.5 h-3.5 stroke-[2]" /> To Railway Station{replayBadge('rail')}
            </span>
            <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600 dark:text-sky-400 shrink-0">
              +{view.headline?.rail_in?.m1 || 0}/m
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-[28px] font-black text-neutral-900 dark:text-neutral-50 leading-tight">
            {fmt(view.headline?.rail_in?.total)}
          </div>
          <div className="text-[10.5px] sm:text-[11px] font-medium text-neutral-500 dark:text-neutral-400 flex items-center justify-between mt-1 pt-0.5 border-t border-neutral-100 dark:border-[#222735]">
            <span>5m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.rail_in?.m5 ?? 0}</strong></span>
            <span>15m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.rail_in?.m15 ?? 0}</strong></span>
          </div>
        </div>

        {/* Arriving from the railway station (people walking out of it) */}
        <div title="People walking out of the railway station today (arriving, towards the temple)" className="px-2.5 py-2 rounded-lg bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs hover:border-neutral-400 transition-colors">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-neutral-800 dark:text-neutral-200 truncate">
              <Train className="w-3.5 h-3.5 stroke-[2] text-neutral-400" /> From Railway Station{replayBadge('rail')}
            </span>
            <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-[#232835] text-neutral-600 dark:text-neutral-400 shrink-0">
              {view.headline?.rail_out?.m1 || 0}/m
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-[28px] font-black text-neutral-900 dark:text-neutral-50 leading-tight">
            {fmt(view.headline?.rail_out?.total)}
          </div>
          <div className="text-[10.5px] sm:text-[11px] font-medium text-neutral-500 dark:text-neutral-400 flex items-center justify-between mt-1 pt-0.5 border-t border-neutral-100 dark:border-[#222735]">
            <span>5m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.rail_out?.m5 ?? 0}</strong></span>
            <span>15m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.rail_out?.m15 ?? 0}</strong></span>
          </div>
        </div>

        {/* Returning to the bus station (people walking into it) */}
        <div title="People walking into the PNBS bus station today (returning from the temple)" className="px-2.5 py-2 rounded-lg bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs hover:border-emerald-500/40 transition-colors">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-emerald-600 dark:text-emerald-400 truncate">
              <Bus className="w-3.5 h-3.5 stroke-[2]" /> To Bus Station{replayBadge('bus')}
            </span>
            <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
              +{view.headline?.bus_in?.m1 || 0}/m
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-[28px] font-black text-neutral-900 dark:text-neutral-50 leading-tight">
            {fmt(view.headline?.bus_in?.total)}
          </div>
          <div className="text-[10.5px] sm:text-[11px] font-medium text-neutral-500 dark:text-neutral-400 flex items-center justify-between mt-1 pt-0.5 border-t border-neutral-100 dark:border-[#222735]">
            <span>5m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.bus_in?.m5 ?? 0}</strong></span>
            <span>15m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.bus_in?.m15 ?? 0}</strong></span>
          </div>
        </div>

        {/* Arriving from the bus station (people walking out of it) */}
        <div title="People walking out of the PNBS bus station today (arriving, towards the temple)" className="px-2.5 py-2 rounded-lg bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs hover:border-neutral-400 transition-colors">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-neutral-800 dark:text-neutral-200 truncate">
              <Bus className="w-3.5 h-3.5 stroke-[2] text-neutral-400" /> From Bus Station{replayBadge('bus')}
            </span>
            <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-[#232835] text-neutral-600 dark:text-neutral-400 shrink-0">
              {view.headline?.bus_out?.m1 || 0}/m
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-[28px] font-black text-neutral-900 dark:text-neutral-50 leading-tight">
            {fmt(view.headline?.bus_out?.total)}
          </div>
          <div className="text-[10.5px] sm:text-[11px] font-medium text-neutral-500 dark:text-neutral-400 flex items-center justify-between mt-1 pt-0.5 border-t border-neutral-100 dark:border-[#222735]">
            <span>5m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.bus_out?.m5 ?? 0}</strong></span>
            <span>15m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{view.headline?.bus_out?.m15 ?? 0}</strong></span>
          </div>
        </div>

        {/* Kummaripalem */}
        <div className="px-2.5 py-2 rounded-lg bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs hover:border-amber-500/40 transition-colors">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-amber-600 dark:text-amber-400 truncate">
              <Compass className="w-3.5 h-3.5 stroke-[2]" /> Kummaripalem
            </span>
            <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 shrink-0">
              To Temple
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-[28px] font-black text-orange-500 leading-tight">
            {fmt(data.headline?.kummari_toward?.total)}
          </div>
          <div className="text-[10.5px] sm:text-[11px] font-medium text-neutral-500 dark:text-neutral-400 flex items-center justify-between mt-1 pt-0.5 border-t border-neutral-100 dark:border-[#222735]">
            <span>1m: <strong className="text-orange-500 font-bold">+{data.headline?.kummari_toward?.m1 ?? 0}</strong></span>
            <span>15m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{data.headline?.kummari_toward?.m15 ?? 0}</strong></span>
          </div>
        </div>

        {/* Temple In */}
        <div className="px-2.5 py-2 rounded-lg bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs hover:border-orange-500/40 transition-colors">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-orange-600 dark:text-orange-400 truncate">
              <ArrowUpRight className="w-3.5 h-3.5 stroke-[2]" /> Devotees for Darshan
            </span>
            <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 shrink-0">
              Queue lines
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-[28px] font-black text-orange-500 leading-tight">
            {fmt(temple?.inToday ?? undefined)}
          </div>
          <div className="text-[10.5px] sm:text-[11px] font-medium text-neutral-500 dark:text-neutral-400 flex items-center justify-between mt-1 pt-0.5 border-t border-neutral-100 dark:border-[#222735]">
            <span>Entered today</span>
            <span>15m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{temple?.in15 ?? '—'}</strong></span>
          </div>
        </div>

        {/* Temple Out */}
        <div className="px-2.5 py-2 rounded-lg bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs hover:border-emerald-500/40 transition-colors">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-emerald-600 dark:text-emerald-400 truncate">
              <ArrowDownRight className="w-3.5 h-3.5 stroke-[2]" /> Darshan Completed
            </span>
            <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
              Temple exits
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-[28px] font-black text-emerald-600 dark:text-emerald-400 leading-tight">
            {fmt(temple?.outToday ?? undefined)}
          </div>
          <div className="text-[10.5px] sm:text-[11px] font-medium text-neutral-500 dark:text-neutral-400 flex items-center justify-between mt-1 pt-0.5 border-t border-neutral-100 dark:border-[#222735]">
            <span className="truncate" title="People who came out of the Antharalayam today">Exited from Antharalayam</span>
            <span>15m: <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{temple?.out15 ?? '—'}</strong></span>
          </div>
        </div>
      </div>
      )}

      {/* ================= 3. PILGRIM ROUTE FLOW (8-stage board) ================= */}
      {(
        <NetworkFlowBoard
          summary={isLive ? view : null}
          live={isLive}
          linkTimes={linkTimes}
          onOpenNode={(cps) => {
            setActiveModalGroup(cps as unknown as CheckpointData[]);
            openCameraModal(cps[0] as unknown as CheckpointData, cps[0]?.cameras?.[0]?.id);
          }}
        />
      )}

      {/* ================= 5. CAMERA LIVE STREAM & STATUS TELEMETRY MODAL ================= */}
      {activeModalCheckpoint && (
        <CameraStreamModal
          checkpoint={activeModalCheckpoint}
          checkpoints={activeModalGroup}
          initialCameraId={activeModalCameraId}
          onClose={() => {
            setActiveModalCheckpoint(null);
            setActiveModalCameraId(undefined);
            setActiveModalGroup(undefined);
          }}
        />
      )}
    </div>
  );
};
